import type { ColumnDef } from "../entities/types";
import type { Filter } from "./types";

type AgGridTextFilter = {
  filterType?: "text";
  type?: string;
  filter?: string | null;
};

type AgGridNumberFilter = {
  filterType: "number";
  type?: string;
  filter?: number | null;
  filterTo?: number | null;
};

type AgGridDateFilter = {
  filterType: "date";
  type?: string;
  dateFrom?: string | null;
  dateTo?: string | null;
};

type AgGridSetFilter = {
  filterType: "set";
  values: (string | boolean | null)[];
};

type AgGridMultiFilter = {
  filterType: "multi";
  filterModels: (AgGridColumnFilter | null)[];
};

export type AgGridColumnFilter =
  | AgGridTextFilter
  | AgGridNumberFilter
  | AgGridDateFilter
  | AgGridSetFilter
  | AgGridMultiFilter;

export function translateAgGridFilterModel(
  filterModel:
    | Record<string, AgGridColumnFilter | null | undefined>
    | null
    | undefined,
  columnMap: Map<string, ColumnDef>,
): Filter[] {
  if (!filterModel) return [];

  const filters: Filter[] = [];

  for (const [field, model] of Object.entries(filterModel)) {
    if (!model || !columnMap.has(field)) continue;
    const colType = columnMap.get(field)?.type;
    filters.push(...translateColumnFilter(field, model, colType));
  }

  return filters;
}

function translateColumnFilter(
  field: string,
  model: AgGridColumnFilter,
  colType?: ColumnDef["type"],
): Filter[] {
  if (model.filterType === "multi") {
    const parts = model.filterModels.flatMap((part) =>
      part ? translateColumnFilter(field, part, colType) : [],
    );
    return parts;
  }

  if (model.filterType === "set") {
    const values = model.values.filter(
      (value): value is string | boolean => value != null && value !== "",
    );
    if (values.length === 0) return [];
    return [{ field, op: "in", value: values }];
  }

  if (
    model.filterType === "date" ||
    colType === "timestamp" ||
    colType === "date"
  ) {
    return translateDateFilter(field, model as AgGridDateFilter);
  }

  if (model.filterType === "number" || colType === "number") {
    return translateNumberFilter(field, model as AgGridNumberFilter);
  }

  return translateTextFilter(field, model as AgGridTextFilter);
}

/**
 * UTC midnight for the calendar day in an AG Grid date string.
 * Parses YYYY-MM-DD from the value; does not use `new Date()` on local-format
 * strings (e.g. "2026-04-01 00:00:00"), which would shift the day in UTC+ zones.
 */
function utcDayStart(value: string): string {
  const trimmed = value.trim();
  const datePart = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
  if (!datePart) return value;

  const [year, month, day] = datePart[1].split("-").map(Number);
  if (!isValidUtcCalendarDate(year, month, day)) return value;

  return `${datePart[1]}T00:00:00.000Z`;
}

function utcDayAfter(value: string): string {
  const start = utcDayStart(value);
  const parts = /^(\d{4})-(\d{2})-(\d{2})T00:00:00\.000Z$/.exec(start);
  if (!parts) return value;

  const year = Number(parts[1]);
  const month = Number(parts[2]);
  const day = Number(parts[3]);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}T00:00:00.000Z`;
}

function isValidUtcCalendarDate(
  year: number,
  month: number,
  day: number,
): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
}

function translateDateFilter(field: string, model: AgGridDateFilter): Filter[] {
  const type = model.type ?? "equals";

  switch (type) {
    case "blank":
      return [{ field, op: "isNull" }];
    case "notBlank":
      return [{ field, op: "isNotNull" }];
    case "equals":
      if (!model.dateFrom) return [];
      return [
        { field, op: "gte", value: utcDayStart(model.dateFrom) },
        { field, op: "lt", value: utcDayAfter(model.dateFrom) },
      ];
    case "notEqual":
      if (!model.dateFrom) return [];
      return [{ field, op: "neq", value: model.dateFrom }];
    case "greaterThan":
      if (!model.dateFrom) return [];
      return [{ field, op: "gte", value: utcDayAfter(model.dateFrom) }];
    case "lessThan":
      if (!model.dateFrom) return [];
      return [{ field, op: "lt", value: utcDayStart(model.dateFrom) }];
    case "inRange": {
      const filters: Filter[] = [];
      if (model.dateFrom) {
        filters.push({ field, op: "gte", value: utcDayStart(model.dateFrom) });
      }
      if (model.dateTo) {
        filters.push({ field, op: "lt", value: utcDayAfter(model.dateTo) });
      }
      return filters;
    }
    default:
      return [];
  }
}

function translateNumberFilter(
  field: string,
  model: AgGridNumberFilter,
): Filter[] {
  const type = model.type ?? "equals";

  switch (type) {
    case "blank":
      return [{ field, op: "isNull" }];
    case "notBlank":
      return [{ field, op: "isNotNull" }];
    case "equals":
      if (model.filter == null) return [];
      return [{ field, op: "eq", value: model.filter }];
    case "notEqual":
      if (model.filter == null) return [];
      return [{ field, op: "neq", value: model.filter }];
    case "greaterThan":
      if (model.filter == null) return [];
      return [{ field, op: "gt", value: model.filter }];
    case "greaterThanOrEqual":
      if (model.filter == null) return [];
      return [{ field, op: "gte", value: model.filter }];
    case "lessThan":
      if (model.filter == null) return [];
      return [{ field, op: "lt", value: model.filter }];
    case "lessThanOrEqual":
      if (model.filter == null) return [];
      return [{ field, op: "lte", value: model.filter }];
    case "inRange": {
      const filters: Filter[] = [];
      if (model.filter != null) {
        filters.push({ field, op: "gte", value: model.filter });
      }
      if (model.filterTo != null) {
        filters.push({ field, op: "lte", value: model.filterTo });
      }
      return filters;
    }
    default:
      return [];
  }
}

function translateTextFilter(field: string, model: AgGridTextFilter): Filter[] {
  const type = model.type ?? "contains";

  switch (type) {
    case "blank":
      return [{ field, op: "isNull" }];
    case "notBlank":
      return [{ field, op: "isNotNull" }];
    case "equals":
      if (!model.filter) return [];
      return [{ field, op: "eq", value: model.filter }];
    case "notEqual":
      if (!model.filter) return [];
      return [{ field, op: "neq", value: model.filter }];
    case "contains":
      if (!model.filter) return [];
      return [{ field, op: "contains", value: model.filter }];
    case "startsWith":
      if (!model.filter) return [];
      return [{ field, op: "startsWith", value: model.filter }];
    case "endsWith":
      if (!model.filter) return [];
      return [{ field, op: "endsWith", value: model.filter }];
    case "notContains":
      if (!model.filter) return [];
      return [{ field, op: "notContains", value: model.filter }];
    default:
      return [];
  }
}
